import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { AccountListView, AccountRowView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import {
  AccountingTabs,
  JournalDetail,
  JournalDownloads,
  NotPermitted,
  OfflineList,
  todayIso,
} from '../accounting/common.tsx';
import styles from '../accounting/accounting.module.css';
import {
  accountingKeys,
  accountsQuery,
  closePeriod,
  journalQuery,
  periodJournalQuery,
  periodsQuery,
  putAccount,
} from '../api/accounting.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { LoadFailed } from '../procurement/common.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Accounting (docs/plans/phase3-accounting-and-revenue-share.md §6): the journal of open
// days, closed periods and the chart-of-accounts mapping. The server audits every view and
// download; nothing here names a patient.

function Frame({ children }: { children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'accounting.read'))
    return (
      <>
        <PageHeader title="Accounting" />
        <NotPermitted what="accounting" />
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader
        title="Accounting"
        description="Journals for the accounting package, made from what Wodi recorded. Closed periods never change."
      />
      <AccountingTabs />
      <OfflineList />
      {children}
    </div>
  );
}

function useCanExport(): boolean {
  const { data: session } = useSuspenseQuery(sessionQuery);
  return session !== null && can(session, 'accounting.export');
}

// --- Journal ----------------------------------------------------------------------------------

function JournalContent() {
  const periods = useQuery(periodsQuery);
  const today = todayIso();
  const firstOfMonth = `${today.slice(0, 8)}01`;
  const nextStart = periods.data?.nextStartsOn ?? null;
  const defaultFrom = nextStart !== null && nextStart > firstOfMonth ? nextStart : firstOfMonth;
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(today);
  const chosenFrom = from ?? defaultFrom;
  const ready = chosenFrom !== '' && to !== null && periods.isSuccess;
  const journal = useQuery({ ...journalQuery(chosenFrom, to ?? today), enabled: ready });
  const canExport = useCanExport();
  return (
    <>
      <Section
        title="Days"
        description={
          nextStart === null
            ? 'Any days up to today, at most 92.'
            : `Open days start on ${formatIsoDate(nextStart)}; earlier days are in closed periods.`
        }
      >
        <div className={styles.toolbar}>
          <DateField
            label="From"
            value={chosenFrom}
            maxValue={today}
            {...(nextStart === null ? {} : { minValue: nextStart })}
            onChange={(v) => {
              setFrom(v ?? '');
            }}
          />
          <DateField label="To" value={to} maxValue={today} onChange={setTo} />
        </div>
        {canExport && journal.isSuccess && journal.data.totals.balanced ? (
          <JournalDownloads target={{ from: chosenFrom, to: to ?? today }} />
        ) : null}
      </Section>
      {!ready || journal.isPending ? (
        periods.isError ? (
          <LoadFailed
            error={periods.error}
            fallback="The accounting periods couldn't be loaded."
            onRetry={() => void periods.refetch()}
          />
        ) : (
          <LoadingBlock label="Loading the journal" rows={6} />
        )
      ) : journal.isError ? (
        <LoadFailed
          error={journal.error}
          fallback="The journal couldn't be loaded."
          onRetry={() => void journal.refetch()}
        />
      ) : (
        <JournalDetail journal={journal.data} />
      )}
    </>
  );
}

export function AccountingJournalPage() {
  return (
    <Frame>
      <JournalContent />
    </Frame>
  );
}

// --- Periods ----------------------------------------------------------------------------------

function ClosePeriodSection({
  nextStartsOn,
  latestEndsOn,
}: {
  nextStartsOn: string | null;
  latestEndsOn: string;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [startsOn, setStartsOn] = useState<string | null>(nextStartsOn);
  const [endsOn, setEndsOn] = useState<string | null>(latestEndsOn);
  const [confirming, setConfirming] = useState(false);
  const close = useMutation({
    mutationFn: () => closePeriod({ startsOn: startsOn ?? '', endsOn: endsOn ?? '' }),
    onSuccess: async (period) => {
      await queryClient.invalidateQueries({ queryKey: accountingKeys.all });
      toasts.show({
        title: 'Period closed',
        description: `${formatIsoDate(period.startsOn)} to ${formatIsoDate(period.endsOn)}`,
      });
      setConfirming(false);
    },
  });
  const start = nextStartsOn ?? startsOn;
  return (
    <Section
      title="Close the next period"
      description="Closing freezes the period's journal. Anything that later changes one of its days posts on the first day after it, marked late."
    >
      <div className={styles.toolbar}>
        {nextStartsOn === null ? (
          <DateField
            label="Starts on"
            value={startsOn}
            maxValue={latestEndsOn}
            onChange={setStartsOn}
          />
        ) : (
          <dl className={styles.facts}>
            <dt>Starts on</dt>
            <dd>{formatIsoDate(nextStartsOn)}</dd>
          </dl>
        )}
        <DateField
          label="Ends on"
          value={endsOn}
          maxValue={latestEndsOn}
          {...(start === null ? {} : { minValue: start })}
          description="Yesterday at the latest."
          onChange={setEndsOn}
        />
        <Button
          variant="primary"
          isDisabled={!online || start === null || endsOn === null}
          onPress={() => {
            setConfirming(true);
          }}
        >
          Close period
        </Button>
      </div>
      {confirming && start !== null && endsOn !== null ? (
        <Dialog
          title="Close this period?"
          tone="alert"
          isOpen
          onOpenChange={(open) => {
            if (!open) {
              setConfirming(false);
              close.reset();
            }
          }}
        >
          <p>
            {`${formatIsoDate(start)} to ${formatIsoDate(endsOn)} will be closed. Its journal can't be changed afterwards and the period can't be reopened.`}
          </p>
          {close.isError ? (
            <Notice tone="error">
              {errorText(close.error, "The period wasn't closed. Try again.")}
            </Notice>
          ) : null}
          <DialogActions>
            <Button
              variant="destructive"
              isPending={close.isPending}
              pendingLabel="Closing…"
              isDisabled={!online}
              onPress={() => {
                close.mutate();
              }}
            >
              Close period
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setConfirming(false);
              }}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Section>
  );
}

function PeriodsContent() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const periods = useQuery(periodsQuery);
  if (periods.isPending) return <LoadingBlock label="Loading periods" rows={4} />;
  if (periods.isError)
    return (
      <LoadFailed
        error={periods.error}
        fallback="The accounting periods couldn't be loaded."
        onRetry={() => void periods.refetch()}
      />
    );
  const data = periods.data;
  return (
    <>
      {session !== null && can(session, 'accounting.period.close') ? (
        <ClosePeriodSection nextStartsOn={data.nextStartsOn} latestEndsOn={data.latestEndsOn} />
      ) : null}
      <Section title="Closed periods">
        <TableScroll>
          <Table flat aria-label="Closed periods">
            <TableHeader>
              <Column isRowHeader>Period</Column>
              <Column>Closed</Column>
              <Column numeric>Debits</Column>
              <Column numeric>Entries</Column>
              <Column numeric>Late lines</Column>
            </TableHeader>
            <TableBody
              items={data.periods}
              renderEmptyState={() => 'No period has been closed yet.'}
            >
              {(p) => (
                <Row id={p.id}>
                  <Cell>
                    <Link to="/accounting/periods/$periodId" params={{ periodId: p.id }}>
                      {`${formatIsoDate(p.startsOn)} to ${formatIsoDate(p.endsOn)}`}
                    </Link>
                  </Cell>
                  <Cell>{`${p.closedBy.displayName}, ${formatInstant(p.closedAt)}`}</Cell>
                  <Cell numeric>{money(p.debit)}</Cell>
                  <Cell numeric>{p.entryCount}</Cell>
                  <Cell numeric>{p.lateLineCount}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
    </>
  );
}

export function AccountingPeriodsPage() {
  return (
    <Frame>
      <PeriodsContent />
    </Frame>
  );
}

const periodRoute = getRouteApi('/_app/accounting/periods/$periodId');

function PeriodJournalContent() {
  const { periodId } = periodRoute.useParams();
  const journal = useQuery(periodJournalQuery(periodId));
  const canExport = useCanExport();
  if (journal.isPending) return <LoadingBlock label="Loading the period's journal" rows={6} />;
  if (journal.isError)
    return (
      <LoadFailed
        error={journal.error}
        fallback="The period's journal couldn't be loaded."
        onRetry={() => void journal.refetch()}
      />
    );
  return (
    <>
      <p className={styles.meta}>
        <Link to="/accounting/periods">Back to periods</Link>
      </p>
      <Section
        title={`Closed period ${formatIsoDate(journal.data.from)} to ${formatIsoDate(journal.data.to)}`}
      >
        <p className={styles.meta}>
          This journal was frozen when the period closed. It never changes.
        </p>
        {canExport ? <JournalDownloads target={{ periodId }} /> : null}
      </Section>
      <JournalDetail journal={journal.data} />
    </>
  );
}

export function AccountingPeriodPage() {
  return (
    <Frame>
      <PeriodJournalContent />
    </Frame>
  );
}

// --- Accounts ---------------------------------------------------------------------------------

interface Editing {
  row: AccountRowView;
  scopeId: string | null;
}

function MappingDialog({
  editing,
  payers,
  onClose,
}: {
  editing: Editing;
  payers: AccountListView['payers'];
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [scopeId, setScopeId] = useState<string | null>(editing.scopeId);
  const current = editing.row.mappings.find((m) => m.scopeId === scopeId) ?? null;
  const [code, setCode] = useState(current?.code ?? '');
  const [name, setName] = useState(current?.name ?? editing.row.defaultName);
  const save = useMutation({
    mutationFn: () =>
      putAccount({
        key: editing.row.key,
        scopeId,
        code: code.trim() === '' ? null : code.trim(),
        name: name.trim(),
        version: current?.version ?? null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: accountingKeys.all });
      toasts.show({ title: 'Account mapping saved', description: name.trim() });
      onClose();
    },
  });
  return (
    <Dialog
      title={`Map ${editing.row.defaultName}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>{editing.row.description}</p>
      <div className={styles.form}>
        {editing.row.payerScoped ? (
          <Select
            label="Payer"
            description="Leave as all payers to map the account for every payer at once."
            options={[
              { id: 'all', label: 'All payers' },
              ...payers.map((p) => ({ id: p.id, label: p.name })),
            ]}
            value={scopeId ?? 'all'}
            onChange={(key) => {
              const next = key === 'all' || key === null ? null : String(key);
              setScopeId(next);
              const found = editing.row.mappings.find((m) => m.scopeId === next);
              setCode(found?.code ?? '');
              setName(found?.name ?? editing.row.defaultName);
            }}
          />
        ) : null}
        <TextField
          label="Account code"
          description="As in the accounting package. Leave empty if it has no codes."
          width="sm"
          value={code}
          onChange={setCode}
          maxLength={30}
        />
        <TextField
          label="Account name"
          description="Exactly as in the accounting package, so the import finds it."
          value={name}
          onChange={setName}
          maxLength={120}
          isRequired
        />
      </div>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The mapping wasn't saved. Try again.")}
        </Notice>
      ) : null}
      {online ? null : <Notice tone="info">Saving needs a connection to Wodi.</Notice>}
      <DialogActions>
        <Button
          variant="primary"
          isDisabled={!online || name.trim() === ''}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save mapping
        </Button>
        <Button variant="quiet" onPress={onClose}>
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AccountsContent() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const accounts = useQuery(accountsQuery);
  const [editing, setEditing] = useState<Editing | null>(null);
  const canManage = session !== null && can(session, 'accounting.manage');
  if (accounts.isPending) return <LoadingBlock label="Loading accounts" rows={8} />;
  if (accounts.isError)
    return (
      <LoadFailed
        error={accounts.error}
        fallback="The accounts couldn't be loaded."
        onRetry={() => void accounts.refetch()}
      />
    );
  const rows = accounts.data.accounts.flatMap((a) => {
    const own = a.mappings.find((m) => m.scopeId === null) ?? null;
    return [
      { id: a.key, account: a, mapping: own, scopeName: null as string | null },
      ...a.mappings
        .filter((m) => m.scopeId !== null)
        .map((m) => ({
          id: `${a.key}|${m.scopeId ?? ''}`,
          account: a,
          mapping: m,
          scopeName: m.scopeName,
        })),
    ];
  });
  return (
    <Section
      title="Chart of accounts"
      description="Where each kind of line goes in the accounting package. Until you map one, Wodi uses its default name with no code."
    >
      <TableScroll>
        <Table flat aria-label="Chart of accounts">
          <TableHeader>
            <Column isRowHeader>Wodi account</Column>
            <Column>Code</Column>
            <Column>Account in the package</Column>
            <Column>Action</Column>
          </TableHeader>
          <TableBody items={rows}>
            {(r) => (
              <Row id={r.id}>
                <Cell>
                  {r.scopeName === null
                    ? r.account.defaultName
                    : `${r.account.defaultName}: ${r.scopeName}`}
                  <br />
                  <span className={styles.meta}>{r.account.description}</span>
                </Cell>
                <Cell>
                  {r.mapping?.code === null || r.mapping === null ? (
                    ''
                  ) : (
                    <span className="wodi-id">{r.mapping.code}</span>
                  )}
                </Cell>
                <Cell>
                  {r.mapping === null ? `${r.account.defaultName} (default)` : r.mapping.name}
                </Cell>
                <Cell>
                  {canManage ? (
                    <div className={styles.actions}>
                      <Button
                        dense
                        onPress={() => {
                          setEditing({ row: r.account, scopeId: r.mapping?.scopeId ?? null });
                        }}
                      >
                        Change
                      </Button>
                      {r.account.payerScoped && r.scopeName === null ? (
                        <Button
                          dense
                          variant="quiet"
                          onPress={() => {
                            setEditing({
                              row: r.account,
                              scopeId: accounts.data.payers[0]?.id ?? null,
                            });
                          }}
                        >
                          Map for a payer
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
      {editing === null ? null : (
        <MappingDialog
          editing={editing}
          payers={accounts.data.payers}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

export function AccountingAccountsPage() {
  return (
    <Frame>
      <AccountsContent />
    </Frame>
  );
}
